import React from "react";
import {
  LabelTag,
  ResolutionTag,
  AssigneeSelect,
  DateRenderer,
  type AssigneeOption,
} from "./Components";
import { getAvatarColor } from "@/utils/avatarUtils";
import { Link } from "react-router";
import {
  tooltipTitle,
  pillColor,
  textColor,
} from "@/utils/tableConfigs/VAStatusMapping";
import StatusPill from "@/components/UI/StatusPill";

const titleMap: Record<string, string> = {
  //shown
  issue_id: "Issue ID",
  discrepancy_description: "Issue",
  discrepancy_status: "Issue Status",
  discrepancy_assignee: "Assigned to",
  discrepancy_days_open: "Time Open (Days)",
  discrepancy_data_source: "Source",
  discrepancy_resolution_status: "Resolution",
  subject: "Subject",

  // hidden
  discrepancy_rule_id: "Rule ID",
  site: "Site",
  study_status: "Study Status",
  discrepancy_trial_visit: "Timepoint",
  discrepancy_level: "Level",
  discrepancy_type: "Issue Type",
  discrepancy_category: "Issue Category",
  discrepancy_subcategory: "Issue Subcategory",
  discrepancy_details: "Details",
  discrepancy_vivo_status: "Vivo Status",
  discrepancy_identification_datetime: "Identification Date",
  discrepancy_resolution_datetime: "Resolution Date",
  discrepancy_last_modified_by: "Last Modified By",
};

const getColumnTitle = (dataIndex: string): string => {
  return (
    titleMap[dataIndex] ||
    dataIndex
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  );
};

const getColumnRenderer = (
  dataIndex: string,
  availableAssignees: AssigneeOption[],
  queryKey: string[],
  buildUrl: (path?: string) => string,
  assigneeIdToName?: Record<string, string>,
  onNotificationRefresh?: () => void,
) => {
  const renderMap: Record<
    string,
    (text: any, record?: any) => React.ReactNode
  > = {
    issue_id: (text, record) => (
      <span className="inline-flex items-center gap-1.5">
        {text}
        {record?.is_new && (
          <span className="text-[10px] font-semibold text-[#06709B] bg-[#EAF4F9] px-1.5 py-0.5 rounded">
            NEW
          </span>
        )}
      </span>
    ),
    discrepancy_resolution_status: (text) => <ResolutionTag status={text} />,
    discrepancy_status: (text, record) => (
      <LabelTag
        text={text}
        record={record}
        queryKey={queryKey}
        onAssignmentChange={onNotificationRefresh}
      />
    ),
    discrepancy_assignee: (text, record) => {
      return (
        <div className="px-2 py-1">
          <AssigneeSelect
            availableAssignees={availableAssignees}
            text={text}
            record={record}
            queryKey={queryKey}
            assigneeIdToName={assigneeIdToName}
            onAssignmentChange={onNotificationRefresh}
          />
        </div>
      );
    },

    discrepancy_category: (text) => renderTextFallback(text),
    discrepancy_subcategory: (text) => renderTextFallback(text),
    discrepancy_type: (text, record) => (
      <span data-testid={`issue-drawer-trigger-${record.discrepancy_id}`}>
        {renderTextFallback(text)}
      </span>
    ),
    discrepancy_resolution_datetime: (text) =>
      text ? <DateRenderer dateString={text} /> : "-",
    discrepancy_assignments_last_modified_datetime: (text) =>
      text ? <DateRenderer dateString={text} /> : "-",
    discrepancy_identification_datetime: (text) =>
      text ? <DateRenderer dateString={text} /> : "-",
    discrepancy_days_open: (text) => renderTextFallback(text),
    subject: (text) => {
      return (
        <Link
          className="text-[#08709B]"
          to={buildUrl(`subjects/overview/${text}/data-discrepancies`)}
        >
          {text}
        </Link>
      );
    },
    discrepancy_last_modified_by: (text: any) => {
      if (!text) return "-";
      const initials = text
        .split(" ")
        .map((n: any) => n[0])
        .join("");
      const color = getAvatarColor(text);
      return (
        <div className="flex items-center gap-2 px-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: color }}
          >
            {initials}
          </div>
          <p>{text}</p>
        </div>
      );
    },
    study_status: (text) => {
      return (
        <StatusPill
          text={text}
          tooltipTitle={tooltipTitle}
          pillColor={pillColor}
          textColor={textColor}
        />
      );
    },
  };

  const defaultRenderer = (text: any) => text;

  return renderMap[dataIndex] || defaultRenderer;
};

const getColumnCellProps = (
  dataIndex: string,
  handleRowClick: (record: any) => void,
  record: any,
) => {
  const nonClickableColumns = [
    "discrepancy_assignee",
    "discrepancy_status",
    "subject",
  ];

  if (!nonClickableColumns.includes(dataIndex)) {
    return {
      className: "cursor-pointer",
      onClick: (e: any) => {
        e.stopPropagation();
        handleRowClick(record);
      },
    };
  }

  return undefined;
};

const omittedColumns = [
  "participant_id",
  "discrepancy_assignment_last_modified_by",
  "discrepancy_assignments_last_modified_datetime",
  "trial_nct_id",
  "discrepancy_id",
];

const getColumnWidth = (column: string) => {
  if (
    column === "discrepancy_description" ||
    column === "discrepancy_details"
  ) {
    return 300;
  }
  if (column === "discrepancy_status" || column === "discrepancy_assignee") {
    return 250;
  }

  return Math.max(100, 15 * (getColumnTitle(column).length + 1));
};

export const createColumns = (
  columns: string[],
  handleRowClick: (record: any) => void,
  lockedColumns: number,
  availableAssignees: AssigneeOption[],
  queryKey: string[],
  buildUrl: (path?: string) => string,
  assigneeIdToName?: Record<string, string>,
  onNotificationRefresh?: () => void,
  columnsTitleCreator?: (column: string) => string,
) => {
  return columns
    .filter((column) => !omittedColumns.includes(column))
    .map((column, index) => {
      return {
        title: columnsTitleCreator
          ? columnsTitleCreator(column)
          : getColumnTitle(column),
        dataIndex: column,
        key: column,
        width: getColumnWidth(column),
        fixed: index < lockedColumns ? "left" : undefined,
        onCell: (record: any) =>
          getColumnCellProps(column, handleRowClick, record),
        render: (text: any, record: any) =>
          getColumnRenderer(
            column,
            availableAssignees,
            queryKey,
            buildUrl,
            assigneeIdToName,
            onNotificationRefresh,
          )(text, record),
      };
    });
};

const renderTextFallback = (text: React.ReactNode) => {
  if (!text) return "-";
  return text;
};
